import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faArrowDown } from '@fortawesome/free-solid-svg-icons';

import { HwtButton } from 'components/@common/Buttons/HwtButton';
import { LessonAssignmentActivityContent } from './LessonAssignmentActivityContent';
import { OuterContainerHeader } from './OuterContainerHeader';
import { useAlert, AlertVariant } from 'state/providers/AlertProvider';
import { translate } from 'translations/translator';
import useLessonAssignment from 'state/providers/LessonAssignmentProvider';
import { calcDuration } from 'util/assignment-util';
import { getDayOfWeek } from 'util/date-util';
import LessonAssignmentTranslationVariant from 'translations/variants/lessonAssignment';
import { StepOneEditAssignmentHeader } from './StepOneEditAssignmentHeader';

const { edit } = LessonAssignmentTranslationVariant;
const getTranslation = (key, option?) => translate('lessonAssignment', key, option);

const Panels = ({ data, isAssigned }) => {
  const [{ lessonAssignmentState }, { stepOneFormDispatch, lessonAssignmentDispatch }] =
    useLessonAssignment();

  const classes = useStyles()();
  const [, { openAlert }] = useAlert();

  const handleTogglePanel = id => async () => {
    const errors = await lessonAssignmentDispatch.setActiveForm(id);

    if (errors) {
      openAlert({ body: JSON.stringify(errors), variant: AlertVariant.ERROR });
    }
  };

  const handleAssign = id => () => {
    stepOneFormDispatch.handleAssign(id);
  };

  return (
    <>
      <OuterContainerHeader
        assigned={isAssigned}
        numOfAssigned={data.length}
        dateRange={lessonAssignmentState?.dateRange}
      />

      {data?.map(x => {
        return (
          <Accordion
            key={`expansionPanel-${x.id}`}
            expanded={String(x.id) === lessonAssignmentState.activeFormId}
            onChange={handleTogglePanel(x.id)}
            className={classes.expansionPanel}
          >
            {x.id === lessonAssignmentState.activeFormId && (
              <StepOneEditAssignmentHeader
                key={`stepOne-${x.id}`}
                lessonTitle={(x?.title || x?.lesson.title) ?? ''}
                handleCollapse={handleTogglePanel('')}
                duration={calcDuration(x)}
                noActivities={
                  x.lesson?.sections?.length === 0 ||
                  x.lesson?.sections?.every(s => s.activities.length === 0)
                }
              />
            )}

            {String(x.id) !== lessonAssignmentState.activeFormId && (
              <AccordionSummary key={`summary-${x.id}`} style={{ height: 124 }}>
                <Box style={{ display: 'flex', flex: 1, justifyContent: 'space-between' }}>
                  <Box style={{ display: 'flex', flex: 0.5, alignItems: 'center' }}>
                    <Typography variant="subtitle1" style={{ margin: '0px 10px' }}>
                      {(x?.title || x?.lesson?.title) ?? ''}
                    </Typography>
                  </Box>
                  <div
                    style={{
                      display: 'flex',
                      width: '40%',
                      justifyContent: 'flex-end',
                      alignItems: 'center'
                    }}
                  >
                    <HwtButton variant="text" style={{ textTransform: 'none' }}>
                      <FontAwesomeIcon
                        icon={faArrowDown}
                        style={{ marginRight: '10px', fontSize: '14px' }}
                      />
                      {getTranslation(LessonAssignmentTranslationVariant.expand)}
                    </HwtButton>
                    <HwtButton
                      onClick={handleAssign(x.id)}
                      variant={x.assigned ? 'outlined' : 'contained'}
                      style={{
                        whiteSpace: 'nowrap',
                        padding: '10px !important',
                        width: 'auto',
                        fontSize: '14px !important'
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faEdit}
                        style={{ marginRight: '10px', fontSize: '14px' }}
                      />
                      {x.assigned ? getTranslation(edit) : getTranslation('assign')}{' '}
                      {getTranslation('dayAssignment', {
                        day: getDayOfWeek(new Date(x.startDate))
                      })}
                    </HwtButton>
                  </div>
                </Box>
              </AccordionSummary>
            )}

            {x.lesson?.sections?.length &&
              x.lesson?.sections?.map((section, sectionIdx) => (
                <AccordionDetails style={{ padding: '32px' }} key={`expansionPanel-${sectionIdx}`}>
                  <LessonAssignmentActivityContent section={{ ...section, sectionIdx }} />
                </AccordionDetails>
              ))}
          </Accordion>
        );
      })}
    </>
  );
};

const useStyles = () =>
  makeStyles(() => ({
    expansionPanel: {
      margin: '15px 55px',
      alignSelf: 'center',
      width: '82vw',
      maxWidth: 1356,
      borderRadius: '5px',
      boxShadow:
        '0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)'
    }
  }));

export default Panels;
